/* eslint-disable max-len */
import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'
import { Line } from '~/utils/editor/objects/line.class'
import { ActualVersion as LineProps } from '~/utils/editor/versioning/line'
import { makeShadow } from '~/utils/editor/helpers/Helpers'

export interface LineObject {
  svg: string
  defaultProps: LineProps
  disablePointerHeight?: boolean
  makeObject: (props: Line) => fabricTypes.Group
  updateObject: (props: Line, object: fabricTypes.Group) => void
}

export const PADDING = 10

export const lines: LineObject[] = [
  {
    svg: '<svg width="65" height="1" viewBox="0 0 65 1" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.5H65" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      dash: false,
      width: 1,
    },
    disablePointerHeight: true,
    makeObject: (props: Line) => {
      return new fabric.Group([
        new fabric.Line([ 0, 0, props.length, 0 ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
        layoutManager: new fabric.LayoutManager(new fabric.FixedLayout),
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: props.length,
        y2: 0,
        strokeWidth: props.width,
        stroke: props.color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: -(props.length + props.width) / 2,
        top: -props.width / 2,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="65" height="1" viewBox="0 0 65 1" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.5H65" stroke="black" stroke-dasharray="9 9"/></svg>',
    defaultProps: {
      length: 232,
      dash: true,
      dashSize: [ 9, 9 ],
      width: 1,
    },
    disablePointerHeight: true,
    makeObject: (props: Line) => {
      return new fabric.Group([
        new fabric.Line([ 0, 0, props.length, 0 ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 9, 9 ] : undefined,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: props.length,
        y2: 0,
        strokeWidth: props.width,
        stroke: props.color,
        strokeDashArray: props.dash ? [ 9, 9 ] : undefined,
        left: -(props.length + props.width) / 2,
        top: -props.width / 2,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="65" height="1" viewBox="0 0 65 1" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0.5H65" stroke="black" stroke-dasharray="5 5"/></svg>',
    defaultProps: {
      length: 232,
      dash: true,
      dashSize: [ 5, 5 ],
      width: 1,
    },
    disablePointerHeight: true,
    makeObject: (props: Line) => {
      return new fabric.Group([
        new fabric.Line([ 0, 0, props.length, 0 ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: props.length,
        y2: 0,
        strokeWidth: props.width,
        stroke: props.color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: -(props.length + props.width) / 2,
        top: -props.width / 2,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="66" height="8" viewBox="0 0 66 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 0V8.00029" stroke="black"/><path d="M0 4H64.5" stroke="black"/><path d="M65 0V8.00029" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
    },
    makeObject: (props: Line) => {
      const length = props.length - props.width * 2
      const halfPointerHeight = (props.height - props.width) / 2
      return new fabric.Group([
        new fabric.Line([ 0, PADDING, 0, props.pointerHeight + PADDING ], {
          strokeWidth: props.width,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight - PADDING, length, halfPointerHeight - PADDING ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Line([ length, PADDING, length, props.pointerHeight + PADDING ], {
          strokeWidth: props.width,
          stroke: props.color,
        }),
      ], {
        height: props.height,
        width: length,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const borderStroke = Math.max(Math.min(props.width, props.pointerHeight), 1)
      const length = props.length - borderStroke * 2
      const left = -props.length / 2
      const top = -(props.height + borderStroke) / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: 0,
        y2: props.pointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left,
        top: top + PADDING,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Line>object._objects[2]).set({
        x1: length,
        y1: 0,
        x2: length,
        y2: props.pointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke,
        top: top + PADDING,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="73" height="15" viewBox="0 0 73 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 7H65.5" stroke="black"/><path d="M11.5336 3.53553V10.6066H4.46251V3.53553H11.5336Z" fill="black"/><path d="M68.5355 3.53553V10.6066H61.4645V3.53553H68.5355Z" fill="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
    },
    makeObject: (props: Line) => {
      const length = props.length - props.width * 2
      const halfPointerHeight = (props.height - props.width) / 2
      return new fabric.Group([
        new fabric.Rect({
          fill: props.color,
          width: props.pointerHeight,
          height: props.pointerHeight,
        }),
        new fabric.Line([ props.pointerHeight, halfPointerHeight, length + props.pointerHeight, halfPointerHeight ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Rect({
          fill: props.color,
          width: props.pointerHeight,
          height: props.pointerHeight,
          left: length + props.pointerHeight - 1,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const borderStroke = Math.max(Math.min(props.width, props.pointerHeight), 1)
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Rect>object._objects[0]).set({
        height: props.pointerHeight,
        width: props.pointerHeight,
        fill: color,
        left: left,
        top: top + PADDING,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke * 2,
        y2: 0,
        strokeWidth: props.width,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left - borderStroke / 4,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Rect>object._objects[2]).set({
        height: props.pointerHeight,
        width: props.pointerHeight,
        fill: color,
        left: left + props.length - props.pointerHeight,
        top: top + PADDING,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="67" height="10" viewBox="0 0 67 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 5H62.5" stroke="black"/><path d="M2.5 2.5C3.88071 1.11929 6.11929 1.11929 7.5 2.5C8.88071 3.88071 8.88071 6.11929 7.5 7.5C6.11929 8.88071 3.88071 8.88071 2.5 7.5C1.11929 6.11929 1.11929 3.88071 2.5 2.5Z" fill="black"/><path d="M59.5 2.5C60.8807 1.11929 63.1193 1.11929 64.5 2.5C65.8807 3.88071 65.8807 6.11929 64.5 7.5C63.1193 8.88071 60.8807 8.88071 59.5 7.5C58.1193 6.11929 58.1193 3.88071 59.5 2.5Z" fill="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
    },
    makeObject: (props: Line) => {
      const length = props.length - props.width * 2
      const halfPointerHeight = (props.height - props.width) / 2
      return new fabric.Group([
        new fabric.Rect({
          fill: props.color,
          width: props.pointerHeight,
          height: props.pointerHeight,
          rx: halfPointerHeight,
          ry: halfPointerHeight,
        }),
        new fabric.Line([ props.pointerHeight, halfPointerHeight, length + props.pointerHeight, halfPointerHeight ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Rect({
          fill: props.color,
          width: props.pointerHeight,
          height: props.pointerHeight,
          left: length + props.pointerHeight - 1,
          rx: halfPointerHeight,
          ry: halfPointerHeight,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const borderStroke = Math.max(Math.min(props.width, props.pointerHeight), 1)
      const halfPointerHeight = props.pointerHeight / 2
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Rect>object._objects[0]).set({
        height: props.pointerHeight,
        width: props.pointerHeight,
        fill: color,
        left: left,
        top: top + PADDING,
        rx: halfPointerHeight,
        ry: halfPointerHeight,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: 0,
        y1: 0,
        x2: props.length - props.pointerHeight,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left - borderStroke / 2 + props.pointerHeight / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Rect>object._objects[2]).set({
        height: props.pointerHeight,
        width: props.pointerHeight,
        fill: color,
        left: left + props.length - props.pointerHeight,
        top: top + PADDING,
        rx: halfPointerHeight,
        ry: halfPointerHeight,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="67" height="11" viewBox="0 0 67 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5.5H65.5" stroke="black"/><path d="M5.5 1L1 5.5L5.5 10" stroke="black"/><path d="M61.5 1L66 5.5L61.5 10" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
    },
    makeObject: (props: Line) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.min(props.width, halfPointerHeight)
      const length = props.length - borderStroke * 2
      return new fabric.Group([
        new fabric.Line([ halfPointerHeight, 0, 0, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight, halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Line([ length - halfPointerHeight, 0, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ length, halfPointerHeight, length - halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.max(Math.min(props.width, halfPointerHeight), 1)
      const unknownParam = borderStroke / (4 * Math.sqrt(2))
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: halfPointerHeight,
        y1: 0,
        x2: 0,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left - unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: 0,
        y1: 0,
        x2: halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left - unknownParam,
        top: unknownParam - borderStroke,
      });
      (<fabricTypes.Line>object._objects[2]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke * 2,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left + borderStroke / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Line>object._objects[3]).set({
        x1: props.length - halfPointerHeight,
        y1: 0,
        x2: props.length,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[4]).set({
        x1: props.length,
        y1: 0,
        x2: props.length - halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: unknownParam - borderStroke,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="53" height="53" viewBox="0 0 53 53" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.18164 3.18164L48.79 48.79" stroke="black"/><path d="M9.54526 3.18198H3.1813V9.54594" stroke="black"/><path d="M49.1442 42.7799V49.1439H42.7802" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
      angle: 45,
    },
    makeObject: (props: Line) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.min(props.width, halfPointerHeight)
      const length = props.length - borderStroke * 2
      return new fabric.Group([
        new fabric.Line([ halfPointerHeight, 0, 0, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight, halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Line([ length - halfPointerHeight, 0, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ length, halfPointerHeight, length - halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.max(Math.min(props.width, halfPointerHeight), 1)
      const unknownParam = borderStroke / (4 * Math.sqrt(2))
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: halfPointerHeight,
        y1: 0,
        x2: 0,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left - unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: 0,
        y1: 0,
        x2: halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left - unknownParam,
        top: unknownParam - borderStroke,
      });
      (<fabricTypes.Line>object._objects[2]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke * 2,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left + borderStroke / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Line>object._objects[3]).set({
        x1: props.length - halfPointerHeight,
        y1: 0,
        x2: props.length,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[4]).set({
        x1: props.length,
        y1: 0,
        x2: props.length - halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: unknownParam - borderStroke,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="11" height="67" viewBox="0 0 11 67" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.5 1L5.5 65.5" stroke="black"/><path d="M10 5.5L5.5 1L1 5.5" stroke="black"/><path d="M10 61.5L5.5 66L1 61.5" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
      angle: 90,
    },
    makeObject: (props: Line) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.min(props.width, halfPointerHeight)
      const length = props.length - borderStroke * 2
      return new fabric.Group([
        new fabric.Line([ halfPointerHeight, 0, 0, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight, halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ 0, halfPointerHeight, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Line([ length - halfPointerHeight, 0, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ length, halfPointerHeight, length - halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.max(Math.min(props.width, halfPointerHeight), 1)
      const unknownParam = borderStroke / (4 * Math.sqrt(2))
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: halfPointerHeight,
        y1: 0,
        x2: 0,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left - unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: 0,
        y1: 0,
        x2: halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left - unknownParam,
        top: unknownParam - borderStroke,
      });
      (<fabricTypes.Line>object._objects[2]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke * 2,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left + borderStroke / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Line>object._objects[3]).set({
        x1: props.length - halfPointerHeight,
        y1: 0,
        x2: props.length,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[4]).set({
        x1: props.length,
        y1: 0,
        x2: props.length - halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: unknownParam - borderStroke,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="66" height="11" viewBox="0 0 66 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 5.5H64.5" stroke="black"/><path d="M60.5 1L65 5.5L60.5 10" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
    },
    makeObject: (props: Line) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.min(props.width, halfPointerHeight)
      const length = props.length - borderStroke * 2
      return new fabric.Group([
        new fabric.Line([ 0, halfPointerHeight, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Line([ length - halfPointerHeight, 0, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ length, halfPointerHeight, length - halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.max(Math.min(props.width, halfPointerHeight), 1)
      const unknownParam = borderStroke / (4 * Math.sqrt(2))
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left - borderStroke / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: props.length - halfPointerHeight,
        y1: 0,
        x2: props.length,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[2]).set({
        x1: props.length,
        y1: 0,
        x2: props.length - halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: unknownParam - borderStroke,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="66" height="9" viewBox="0 0 66 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 4.5H64.5" stroke="black"/><path d="M66 4.5L58 0V9L66 4.5Z" fill="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 24,
      dash: false,
      width: 1,
    },
    makeObject: (props: Line) => {
      const length = props.length - props.width * 2
      return new fabric.Group([
        new fabric.Line([ 0, props.pointerHeight, length, props.pointerHeight ], {
          strokeWidth: props.width,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Triangle({
          fill: props.color,
          width: props.pointerHeight,
          height: props.pointerHeight,
          left: props.length / 2 - props.pointerHeight,
          top: PADDING,
          angle: 90,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const borderStroke = Math.max(Math.min(props.width, props.pointerHeight), 1)
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: props.length - props.pointerHeight + 1,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left - borderStroke / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Triangle>object._objects[1]).set({
        fill: color,
        width: props.pointerHeight,
        height: props.pointerHeight,
        left: left + props.length,
        top: top + PADDING,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
  {
    svg: '<svg width="66" height="11" viewBox="0 0 66 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 5.5H64.5" stroke="black" stroke-dasharray="5 5"/><path d="M60.5 1L65 5.5L60.5 10" stroke="black"/></svg>',
    defaultProps: {
      length: 232,
      pointerHeight: 44,
      dash: true,
      dashSize: [ 5, 5 ],
      width: 1,
    },
    makeObject: (props: Line) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.min(props.width, halfPointerHeight)
      const length = props.length - borderStroke * 2
      return new fabric.Group([
        new fabric.Line([ 0, halfPointerHeight, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
          strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        }),
        new fabric.Line([ length - halfPointerHeight, 0, length, halfPointerHeight ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
        new fabric.Line([ length, halfPointerHeight, length - halfPointerHeight, halfPointerHeight * 2 ], {
          strokeWidth: borderStroke,
          stroke: props.color,
        }),
      ], {
        height: props.height,
        shadow: makeShadow(props),
        objectCaching: false,
      })
    },
    updateObject: (props: Line, object: fabricTypes.Group) => {
      const halfPointerHeight = props.pointerHeight / 2
      const borderStroke = Math.max(Math.min(props.width, halfPointerHeight), 1)
      const unknownParam = borderStroke / (4 * Math.sqrt(2))
      const left = -props.length / 2
      const top = -props.height / 2
      const color = props.color;
      (<fabricTypes.Line>object._objects[0]).set({
        x1: 0,
        y1: 0,
        x2: props.length - borderStroke,
        y2: 0,
        strokeWidth: borderStroke,
        stroke: color,
        strokeDashArray: props.dash ? [ 5, 5 ] : undefined,
        left: left - borderStroke / 2,
        top: -borderStroke / 2,
      });
      (<fabricTypes.Line>object._objects[1]).set({
        x1: props.length - halfPointerHeight,
        y1: 0,
        x2: props.length,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: top - unknownParam + PADDING,
      });
      (<fabricTypes.Line>object._objects[2]).set({
        x1: props.length,
        y1: 0,
        x2: props.length - halfPointerHeight,
        y2: halfPointerHeight,
        strokeWidth: borderStroke,
        stroke: color,
        left: left + props.length - borderStroke - halfPointerHeight + unknownParam,
        top: unknownParam - borderStroke,
      })
      object.set({
        scaleX: 1,
        scaleY: 1,
        left: object.left,
        top: object.top,
        width: props.length,
        height: props.height,
        shadow: makeShadow(props),
      })
    },
  },
]
